import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '@/lib/supabase';
import { useVelvet } from '@/context/VelvetContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Loader2, MapPin, Save } from 'lucide-react';
import { toast } from 'sonner';

const WINDOW_OPTIONS = [
  { m: 30,  label: '30 min' },
  { m: 60,  label: '1 hour' },
  { m: 90,  label: '90 min' },
  { m: 120, label: '2 hours (default)' },
  { m: 180, label: '3 hours' },
  { m: 240, label: '4 hours' },
];

/**
 * Guided Draft creation.
 * - Delivery-only. Fulfillment fixed to Delivery.
 * - When an existing customer is picked, prefill recipient_name/phone/address ONLY on
 *   first pick (never overwrite an operator edit). The operator can then explicitly
 *   save the current recipient address back to the customer via a button.
 */
export default function QuotationBuilder() {
  const navigate = useNavigate();
  const { customers } = useVelvet();
  const [busy, setBusy] = useState(false);
  const [savingAddr, setSavingAddr] = useState(false);
  const [useNew, setUseNew] = useState(false);
  const [prefillFromCustomerId, setPrefillFromCustomerId] = useState(null);
  const [form, setForm] = useState({
    customer_id: '',
    new_customer: { name: '', phone: '', email: '', address: '' },
    type: 'Retail',
    fulfillment: 'Delivery',
    requested_date: '',
    delivery_window_minutes: 120,
    recipient_name: '',
    recipient_address: '',
    recipient_phone: '',
    delivery_instructions: '',
    card_message: '',
    internal_notes: '',
  });

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const updateNew = (k, v) => setForm(f => ({ ...f, new_customer: { ...f.new_customer, [k]: v } }));

  const selectedCustomer = useMemo(
    () => customers.find(c => c.id === form.customer_id),
    [customers, form.customer_id],
  );

  // Prefill recipient fields when an existing customer is picked for the FIRST time.
  // We only fill blank fields — never overwrite something the operator already typed.
  useEffect(() => {
    if (useNew) return;
    if (!selectedCustomer) return;
    if (prefillFromCustomerId === selectedCustomer.id) return;
    setPrefillFromCustomerId(selectedCustomer.id);
    setForm(f => ({
      ...f,
      recipient_name: f.recipient_name || selectedCustomer.name || '',
      recipient_phone: f.recipient_phone || selectedCustomer.phone || '',
      recipient_address: f.recipient_address || selectedCustomer.address || '',
    }));
    if (selectedCustomer.address) {
      toast.success('Prefilled from customer\u2019s saved address — edit if the recipient differs.');
    }
  }, [selectedCustomer, useNew, prefillFromCustomerId]);

  const addressDiffers = !!(
    selectedCustomer &&
    form.recipient_address &&
    (selectedCustomer.address || '').trim() !== form.recipient_address.trim()
  );

  const saveAddressToCustomer = async () => {
    if (!selectedCustomer || !form.recipient_address.trim()) return;
    if (!window.confirm(`Replace ${selectedCustomer.name}\u2019s saved default address?`)) return;
    setSavingAddr(true);
    try {
      await apiFetch(`/customers/${selectedCustomer.id}/address`, {
        method: 'PATCH',
        body: JSON.stringify({ address: form.recipient_address.trim() }),
      });
      toast.success('Customer default address updated');
    } catch (e) { toast.error(e.message); }
    setSavingAddr(false);
  };

  const submit = async () => {
    if (!useNew && !form.customer_id) return toast.error('Please pick an existing customer or add a new one');
    if (useNew && !form.new_customer.name.trim()) return toast.error('New customer needs a name');
    if (!form.requested_date) return toast.error('Requested delivery date & time is required');
    if (!form.recipient_name.trim()) return toast.error('Recipient name is required');
    if (!form.recipient_phone.trim()) return toast.error('Recipient phone is required');
    if (!form.recipient_address.trim()) return toast.error('Delivery address is required');
    setBusy(true);
    try {
      const payload = {
        type: form.type,
        fulfillment: 'Delivery',
        requested_date: new Date(form.requested_date).toISOString(),
        delivery_window_minutes: Number(form.delivery_window_minutes) || 120,
        recipient_name: form.recipient_name,
        recipient_address: form.recipient_address,
        recipient_phone: form.recipient_phone,
        delivery_instructions: form.delivery_instructions || null,
        card_message: form.card_message || null,
        internal_notes: form.internal_notes || null,
      };
      if (useNew) payload.new_customer = form.new_customer;
      else payload.customer_id = form.customer_id;
      const r = await apiFetch('/quotations', { method: 'POST', body: JSON.stringify(payload) });
      toast.success(`Draft ${r.number} created`);
      navigate(`/quotations/${r.id}`);
    } catch (e) {
      toast.error(e.message);
    }
    setBusy(false);
  };

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-8 lg:py-12 max-w-[900px]" data-testid="quotation-builder-page">
      <button onClick={() => navigate('/quotations')} className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground velvet-eyebrow mb-6" data-testid="builder-back">
        <ArrowLeft className="h-3.5 w-3.5" /> Quotations
      </button>
      <div className="velvet-eyebrow mb-3">New quotation</div>
      <h1 className="velvet-page-title mb-10">Start a draft</h1>

      <div className="space-y-8">
        <Section title="Customer">
          <div className="flex items-center gap-3 text-xs mb-3">
            <label className="flex items-center gap-1.5">
              <input type="radio" checked={!useNew} onChange={() => setUseNew(false)} data-testid="use-existing-customer" /> Existing
            </label>
            <label className="flex items-center gap-1.5">
              <input type="radio" checked={useNew} onChange={() => setUseNew(true)} data-testid="use-new-customer" /> New
            </label>
          </div>
          {!useNew ? (
            <div>
              <Label>Existing customer</Label>
              <select
                value={form.customer_id}
                onChange={e => update('customer_id', e.target.value)}
                className="w-full border border-border rounded-sm px-3 py-2 bg-background text-sm mt-1"
                data-testid="customer-select"
              >
                <option value="">— select —</option>
                {(customers || []).map(c => (
                  <option key={c.id} value={c.id}>{c.name} · {c.email || c.phone || '—'}</option>
                ))}
              </select>
              {selectedCustomer && (
                <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1.5" data-testid="customer-saved-address">
                  <MapPin className="h-3 w-3" />
                  {selectedCustomer.address ? `Saved address: ${selectedCustomer.address}` : 'No saved address on file'}
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name*"><Input value={form.new_customer.name} onChange={e => updateNew('name', e.target.value)} className="rounded-sm" data-testid="newc-name" /></Field>
              <Field label="Phone"><Input value={form.new_customer.phone} onChange={e => updateNew('phone', e.target.value)} className="rounded-sm" data-testid="newc-phone" /></Field>
              <Field label="Email"><Input value={form.new_customer.email} onChange={e => updateNew('email', e.target.value)} className="rounded-sm" data-testid="newc-email" /></Field>
              <Field label="Address"><Input value={form.new_customer.address} onChange={e => updateNew('address', e.target.value)} className="rounded-sm" data-testid="newc-address" /></Field>
            </div>
          )}
        </Section>

        <Section title="Quotation">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type">
              <select value={form.type} onChange={e => update('type', e.target.value)} className="w-full border border-border rounded-sm px-3 py-2 bg-background text-sm mt-1" data-testid="quote-type">
                <option value="Retail">Retail floral</option>
                <option value="Event">Event / custom</option>
              </select>
            </Field>
            <Field label="Fulfillment">
              <div className="w-full border border-border rounded-sm px-3 py-2 bg-[hsl(var(--muted))]/40 text-sm mt-1 flex items-center justify-between" data-testid="quote-fulfillment-fixed">
                <span>Delivery</span>
                <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Delivery-only</span>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">Velvet Bloom Flowers is delivery-only online. Pickup is not offered.</p>
            </Field>
            <Field label="Requested delivery start*">
              <Input type="datetime-local" value={form.requested_date} onChange={e => update('requested_date', e.target.value)} className="rounded-sm" data-testid="quote-requested-date" />
            </Field>
            <Field label="Delivery window">
              <select
                value={form.delivery_window_minutes}
                onChange={e => update('delivery_window_minutes', Number(e.target.value))}
                className="w-full border border-border rounded-sm px-3 py-2 bg-background text-sm mt-1"
                data-testid="quote-delivery-window"
              >
                {WINDOW_OPTIONS.map(o => <option key={o.m} value={o.m}>{o.label}</option>)}
              </select>
              <p className="text-[11px] text-muted-foreground mt-1">Delivery arrives within this window from the requested start time.</p>
            </Field>
          </div>
        </Section>

        <Section title="Delivery details">
          <div className="text-[11px] text-muted-foreground mb-3">
            Prefilled from the customer if a saved address exists. Because the purchaser and recipient can differ, everything remains editable — no changes are pushed back to the customer without an explicit action.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Recipient name*"><Input value={form.recipient_name} onChange={e => update('recipient_name', e.target.value)} className="rounded-sm" data-testid="recipient-name" /></Field>
            <Field label="Recipient phone*"><Input value={form.recipient_phone} onChange={e => update('recipient_phone', e.target.value)} className="rounded-sm" data-testid="recipient-phone" /></Field>
            <Field label="Delivery address*" full>
              <Input value={form.recipient_address} onChange={e => update('recipient_address', e.target.value)} className="rounded-sm" data-testid="recipient-address" />
              {!useNew && selectedCustomer && addressDiffers && (
                <button
                  type="button"
                  disabled={savingAddr}
                  onClick={saveAddressToCustomer}
                  className="mt-2 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] text-[hsl(var(--muted-green-dark))] hover:underline underline-offset-4 disabled:opacity-50"
                  data-testid="save-address-to-customer"
                >
                  <Save className="h-3 w-3" />
                  {savingAddr ? 'Saving…' : `Save this address to ${selectedCustomer.name}`}
                </button>
              )}
            </Field>
            <Field label="Delivery instructions (optional)" full><Textarea rows={2} value={form.delivery_instructions} onChange={e => update('delivery_instructions', e.target.value)} className="rounded-sm" placeholder="Gate code, doorman, leave at door…" data-testid="delivery-instructions" /></Field>
          </div>
        </Section>

        <Section title="Message & notes">
          <div className="space-y-3">
            <Field label="Card message (customer-facing)"><Textarea rows={2} value={form.card_message} onChange={e => update('card_message', e.target.value)} className="rounded-sm" data-testid="card-message" /></Field>
            <Field label="Internal notes (never shared with customer)"><Textarea rows={2} value={form.internal_notes} onChange={e => update('internal_notes', e.target.value)} className="rounded-sm" data-testid="internal-notes" /></Field>
          </div>
        </Section>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" className="rounded-sm" onClick={() => navigate('/quotations')} data-testid="builder-cancel">Cancel</Button>
          <Button
            disabled={busy}
            onClick={submit}
            className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
            data-testid="builder-save-draft"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Save as draft
          </Button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="velvet-section-title mb-4">{title}</h2>
      <div>{children}</div>
    </div>
  );
}
function Field({ label, children, full }) {
  return (
    <div className={full ? 'col-span-2' : ''}>
      <Label>{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
